import { get } from 'lodash';
import { GridColumn } from 'src/components/GridWrapper';
import { IReceiptListItem } from 'src/types/Receipt';

import * as XLSX from 'xlsx';
import { formatViewReceipts } from './transformation';

interface ExportProps {
  columns: string[];
  rows: any[];
  fileName: string;
  columnMapping?: Record<string, string>;
  sheetName?: string;
}

const receiptListLineItemsWorkbookColumns = (index: number) => [
  {
    title: ' PO #',
    field: 'poNum',
  },
  {
    title: 'Receipt #',
    field: 'receiptId',
  },
  {
    title: 'Receipt Status',
    field: 'receiptStatus',
  },
  {
    title: 'Buyer Name',
    field: 'buyerName',
  },
  {
    title: 'Receipt Status Date',
    field: 'receiptStatusDate',
  },
  {
    title: 'Supplier Name',
    field: 'supplierName',
  },
  {
    title: 'PO TTL Qty',
    field: 'poQty',
  },
  {
    title: 'Receipt TTL Qty',
    field: 'receiptTTLQty',
  },
  {
    title: 'Current Receipt Qty',
    field: 'currentReceiptQty',
  },
  {
    title: 'PO Status',
    field: 'poStatus',
  },
  {
    title: 'PO Date',
    field: 'poDate',
  },
  {
    title: '% Received',
    field: 'percentReceived',
  },
  {
    title: 'Receipt Date',
    field: 'receiptDate',
  },
  {
    title: 'Item #',
    field: `lineItems.${index}.item`,
  },
  {
    title: 'Item Description',
    field: `lineItems.${index}.itemDescription`,
  },
  {
    title: 'GL Code',
    field: `lineItems.${index}.poglCode`,
  },
  {
    title: 'UOM',
    field: `lineItems.${index}.uom`,
  },
  {
    title: 'Item Note',
    field: `lineItems.${index}.itemNoteValue`,
  },
];

const exportToExcel = ({
  columns,
  rows,
  fileName,
  columnMapping,
  sheetName = 'Sheet1',
}: ExportProps): void => {
  const wb = XLSX.utils.book_new();
  const headerRow = columns.map((col) => (columnMapping && columnMapping[col]) || col);
  const rowData = rows?.map((row) =>
    columns?.reduce((acc, col) => ({ ...acc, [col]: row[col] }), {})
  );
  const data = [headerRow, ...rowData.map((row) => Object?.values(row))];
  const ws = XLSX.utils.aoa_to_sheet(data);

  ws['!cols'] = fitToColumn(data);

  XLSX.utils.book_append_sheet(wb, ws, sheetName);
  XLSX.writeFile(wb, `${fileName}.xlsx`);
};

export const exportViewReceipts = (
  receipts: IReceiptListItem[],
  columns: GridColumn<any>[],
  companyName: string
) => {
  const headerRow = columns.map((column) => column.title);
  const lineItemHeaderRow = receiptListLineItemsWorkbookColumns(0).map((column) => column.title);
  const formattedReceipts = formatViewReceipts(receipts, companyName);

  const { rows, lineItemRows } = formattedReceipts.reduce<{
    rows: string[][];
    lineItemRows: string[][];
  }>(
    (acc, receiptListItem) => {
      const newLineItemRows =
        receiptListItem.lineItems?.map((_, index) =>
          receiptListLineItemsWorkbookColumns(index).map((column) =>
            (get(receiptListItem, column.field) ?? '')?.toString()
          )
        ) || [];
      acc.lineItemRows = [...acc.lineItemRows, ...newLineItemRows];

      acc.rows.push(
        columns.map((column) =>
          (receiptListItem?.[column.field as keyof IReceiptListItem] ?? '')?.toString()
        )
      );

      return acc;
    },
    { rows: [], lineItemRows: [] }
  );

  const ws1Data = [headerRow, ...rows];
  const ws2Data = [lineItemHeaderRow, ...lineItemRows];

  const wb = XLSX.utils.book_new();

  const ws1 = XLSX.utils.aoa_to_sheet(ws1Data);
  ws1['!cols'] = fitToColumn(ws1Data);
  const ws2 = XLSX.utils.aoa_to_sheet(ws2Data);
  ws2['!cols'] = fitToColumn(ws2Data);

  XLSX.utils.book_append_sheet(wb, ws1, 'Receipts');
  XLSX.utils.book_append_sheet(wb, ws2, 'Receipt Line Items');

  XLSX.writeFile(wb, `view-receipts.xlsx`);
};

const fitToColumn = (arrayOfArray: any[][]): { wch: number }[] =>
  arrayOfArray[0].map((_, i: number) => ({
    wch: Math.max(...arrayOfArray.map((a2: any[]) => (a2[i] ? a2[i].toString().length : 0))),
  }));

export default exportToExcel;
